import { useCallback, useEffect, useRef, useState } from 'react';

import { useTranslation } from '@/i18n';
import {
  monthlyCutsSendToPayrollService,
  mapPayrollSubmissionSummary,
  PAYROLL_JOB_STATUS,
  TERMINAL_PAYROLL_JOB_STATUSES,
} from '@/api/domains/attendance';
import type {
  LC_GetPayrollSubmissionResultOut,
  PayrollSubmissionSummary,
} from '@/api/domains/attendance';
import { useUI } from '@/hooks/ui/useUI';
import { useAbortController } from '@/hooks/ui/useAbortController';


// Cadencia del sondeo del resultado del job de envío a remuneraciones.
const POLLING_INTERVAL_MS = 2000;
// Tope de espera del sondeo. Si el job no alcanza un estado terminal en este tiempo, se corta el
// polling y se informa demora, para no dejar el spinner del diálogo colgado indefinidamente.
const POLLING_MAX_DURATION_MS = 5 * 60 * 1000;

// Desenlaces posibles del sondeo del job (estado terminal alcanzado o tope de tiempo agotado).
type PollOutcome =
  | { kind: 'completed'; data: LC_GetPayrollSubmissionResultOut }
  | { kind: 'timeout' };

interface UseSendToPayrollParams {
  // Cortes a enviar (incluye los cerrados: el backend los reporta como omitidos en el resultado).
  selectedCutIds: number[];
  // Se invoca con el resumen del backend para que el llamador muestre el resultado por corte
  // (fase 2 del diálogo) y refresque el listado / limpie la selección.
  onSuccess: (summary: PayrollSubmissionSummary) => void;
}

export interface UseSendToPayrollResult {
  isSubmitting: boolean;
  canSubmit: boolean;
  handleSubmit: () => Promise<void>;
}

// Dispara el envío de conceptos a remuneraciones para los cortes seleccionados. El flujo es
// asíncrono por dentro (POST encola un job → sondeo del resultado cada ~2s), pero conserva el mismo
// UX que el envío síncrono previo: isSubmitting permanece true durante todo el POST + polling para
// que el diálogo mantenga el spinner, y handleSubmit resuelve recién cuando el job termina. Cuando
// el job se completa, mapea el resultado a dominio, informa un resumen (éxito total / parcial) por
// toast y delega el detalle por corte a onSuccess. En error/fallo/demora muestra el toast y no
// invoca onSuccess, dejando el diálogo en la fase de confirmación.
export function useSendToPayroll({
  selectedCutIds,
  onSuccess,
}: UseSendToPayrollParams): UseSendToPayrollResult {
  const { t: translate } = useTranslation();
  const { showApiError, showError, showSuccess, showWarning } = useUI();
  const { createSignal } = useAbortController();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const intervalRef = useRef<number | null>(null);
  const timeoutRef = useRef<number | null>(null);

  const canSubmit = selectedCutIds.length > 0 && !isSubmitting;

  const clearPolling = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Al desmontar, detiene los timers; el AbortController aborta además el request en vuelo.
  useEffect(() => clearPolling, [clearPolling]);

  // Sondea el resultado del job cada POLLING_INTERVAL_MS hasta un estado terminal o el tope de
  // tiempo. Ignora respuestas abortadas (nuevo signal / desmontaje) y reintenta en el siguiente
  // tick ante errores transitorios; resuelve al alcanzar un estado terminal o agotar el tope.
  const pollJobResult = useCallback(
    (jobId: string): Promise<PollOutcome> =>
      new Promise<PollOutcome>((resolve) => {
        let settled = false;
        const settle = (outcome: PollOutcome) => {
          if (settled) return;
          settled = true;
          clearPolling();
          resolve(outcome);
        };

        const tick = async () => {
          const signal = createSignal();
          const response = await monthlyCutsSendToPayrollService.getResult(jobId, signal);
          if (response.error?.code === 'ABORTED') return;
          if (!response.success || !response.data) return;
          if (TERMINAL_PAYROLL_JOB_STATUSES.has(response.data.status)) {
            settle({ kind: 'completed', data: response.data });
          }
        };

        timeoutRef.current = window.setTimeout(
          () => settle({ kind: 'timeout' }),
          POLLING_MAX_DURATION_MS,
        );
        void tick();
        intervalRef.current = window.setInterval(() => void tick(), POLLING_INTERVAL_MS);
      }),
    [createSignal, clearPolling],
  );

  const handleSubmit = useCallback(async () => {
    if (submittingRef.current || selectedCutIds.length === 0) return;
    submittingRef.current = true;
    setIsSubmitting(true);

    try {
      // 1) Encola el job de envío (202 con jobId). El spinner se mantiene mientras dura el proceso.
      const submitResponse = await monthlyCutsSendToPayrollService.send(
        { monthlyCutIds: selectedCutIds },
        createSignal(),
      );
      if (submitResponse.error?.code === 'ABORTED') return;
      if (!submitResponse.success || !submitResponse.data) {
        showApiError(
          submitResponse,
          translate('attendance:monthlyCutsSendToPayroll.messages.sendError'),
        );
        return;
      }

      // 2) Sondea el resultado hasta que el job termine o se agote el tope de tiempo.
      const outcome = await pollJobResult(submitResponse.data.jobId);

      if (outcome.kind === 'timeout') {
        showError(translate('attendance:monthlyCutsSendToPayroll.messages.jobTimeout'));
        return;
      }

      const { status, result } = outcome.data;

      // Estado terminal no exitoso (Fallido / Detenido / Expirado) o completado sin payload.
      if (status !== PAYROLL_JOB_STATUS.COMPLETED || !result) {
        showError(translate('attendance:monthlyCutsSendToPayroll.messages.jobFailed'));
        return;
      }

      const summary = mapPayrollSubmissionSummary(result);

      // Resumen por toast: éxito total cuando todo se envió, o advertencia parcial cuando hubo
      // cortes omitidos (cerrados), con error o no encontrados.
      const hasIssues =
        summary.skippedClosedCount > 0 ||
        summary.errorCount > 0 ||
        summary.notFoundCount > 0;

      if (!hasIssues) {
        showSuccess(
          translate('attendance:monthlyCutsSendToPayroll.messages.sendSuccess', {
            count: summary.sentCount,
          }),
        );
      } else {
        showWarning(
          translate('attendance:monthlyCutsSendToPayroll.messages.sendPartial', {
            sent: summary.sentCount,
            skipped: summary.skippedClosedCount,
            errors: summary.errorCount,
            notFound: summary.notFoundCount,
          }),
        );
      }

      onSuccess(summary);
    } finally {
      clearPolling();
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [
    selectedCutIds,
    createSignal,
    pollJobResult,
    clearPolling,
    showApiError,
    showError,
    showSuccess,
    showWarning,
    translate,
    onSuccess,
  ]);

  return { isSubmitting, canSubmit, handleSubmit };
}

export default useSendToPayroll;
